import React, { useState, useEffect } from 'react';
import { X, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface MediaFile {
  path: string;
  type: 'image' | 'video';
  status: 'checking' | 'success' | 'error';
  size?: number;
  error?: string;
}

export default function MediaDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  const expectedFiles: Omit<MediaFile, 'status'>[] = [
    { path: '/hero-video.mp4', type: 'video' },
    { path: '/portfolio/Propertymasters.png', type: 'image' },
    { path: '/portfolio/sorento.png', type: 'image' },
    { path: '/portfolio/nebenki.de.png', type: 'image' },
    { path: '/portfolio/Litholog.png', type: 'image' },
    { path: '/portfolio/racketbuild.png', type: 'image' }
  ];

  const checkMediaFiles = async () => {
    setIsChecking(true);
    const results: MediaFile[] = [];

    for (const file of expectedFiles) {
      const mediaFile: MediaFile = { ...file, status: 'checking' };
      results.push(mediaFile);
      setMediaFiles([...results]);

      try {
        const response = await fetch(file.path, { method: 'HEAD' });
        
        if (response.ok) {
          mediaFile.status = 'success';
          mediaFile.size = parseInt(response.headers.get('content-length') || '0');
        } else {
          mediaFile.status = 'error';
          mediaFile.error = `HTTP ${response.status}`;
        }
      } catch (error) {
        mediaFile.status = 'error';
        mediaFile.error = error instanceof Error ? error.message : 'Unknown error';
      }

      setMediaFiles([...results]);
    }

    setIsChecking(false);
  };

  useEffect(() => {
    if (isOpen && mediaFiles.length === 0) {
      checkMediaFiles();
    }
  }, [isOpen]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: MediaFile['status']) => {
    switch (status) {
      case 'checking':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-400" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-400" />;
    }
  };

  const successCount = mediaFiles.filter(f => f.status === 'success').length;
  const errorCount = mediaFiles.filter(f => f.status === 'error').length;

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg z-50 transition-colors"
        title="Media Debug Panel"
      >
        <AlertCircle className="h-5 w-5" />
      </button>

      {/* Debug Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 text-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold">Media Files Debug Panel</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Stats */}
            <div className="p-4 border-b border-gray-700 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-400">{successCount}</div>
                <div className="text-sm text-gray-400">Available</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{errorCount}</div>
                <div className="text-sm text-gray-400">Missing</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-400">{expectedFiles.length}</div>
                <div className="text-sm text-gray-400">Total</div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-b border-gray-700">
              <button
                onClick={checkMediaFiles}
                disabled={isChecking}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
                {isChecking ? 'Checking...' : 'Refresh Check'}
              </button>
            </div>

            {/* File List */}
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {mediaFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg"
                  >
                    {getStatusIcon(file.status)}
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm truncate">{file.path}</div>
                      <div className="text-xs text-gray-400 flex gap-4">
                        <span>{file.type}</span>
                        {file.size && <span>{formatFileSize(file.size)}</span>}
                        {file.error && <span className="text-red-400">{file.error}</span>}
                      </div>
                    </div>

                    {file.status === 'success' && file.type === 'image' && (
                      <img
                        src={file.path}
                        alt="Preview"
                        className="w-12 h-8 object-cover rounded"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Instructions */}
              {errorCount > 0 && (
                <div className="mt-6 p-4 bg-red-900/20 border border-red-800 rounded-lg">
                  <h4 className="font-semibold text-red-400 mb-2">Missing Files Detected</h4>
                  <div className="text-sm text-gray-300 space-y-2">
                    <p>Some media files are missing from the public directory. To fix this:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li>Ensure all files are placed in the <code className="bg-gray-800 px-1 rounded">frontend/public</code> directory</li>
                      <li>Check file names and paths are correct (case-sensitive)</li>
                      <li>Verify file formats are supported (MP4/WebM for videos, JPG/PNG for images)</li>
                      <li>Restart the development server after adding files</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
